import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils.js';

const ALL_STAGES = [
  'NEW_LEAD', 'NEW_BUSINESS', 'INITIATED', 'COLLECTING', 'READY_TO_QUOTE',
  'QUOTING', 'PRESENTED', 'BINDING', 'BOUND', 'RENEWAL_DUE', 'PAID',
  'ON_HOLD', 'NEEDS_ATTENTION', 'UPCOMING', 'EXPIRED', 'LOST', 'CANCELLED',
];

const TERMINAL_STAGES = new Set(['LOST', 'CANCELLED']);

const DISPLAY = {
  NEW_LEAD: 'New lead',
  NEW_BUSINESS: 'New business',
  INITIATED: 'Initiated',
  COLLECTING: 'Collecting',
  READY_TO_QUOTE: 'Ready to quote',
  QUOTING: 'Quoting',
  PRESENTED: 'Presented',
  BINDING: 'Binding',
  BOUND: 'Bound',
  RENEWAL_DUE: 'Renewal due',
  PAID: 'Paid',
  ON_HOLD: 'On hold',
  NEEDS_ATTENTION: 'Needs attention',
  UPCOMING: 'Upcoming',
  EXPIRED: 'Expired',
  LOST: 'Lost',
  CANCELLED: 'Cancelled',
};

export function defaultStageFilter() {
  return ALL_STAGES.filter(s => !TERMINAL_STAGES.has(s));
}

export { ALL_STAGES };

export default function StageFilterChip({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const isDefault = selected.length === ALL_STAGES.length - TERMINAL_STAGES.size
    && !selected.some(s => TERMINAL_STAGES.has(s));
  const activeCount = isDefault ? 0 : selected.length;

  function toggleStage(stage) {
    onChange(
      selected.includes(stage)
        ? selected.filter(s => s !== stage)
        : [...selected, stage]
    );
  }

  function selectAll() {
    onChange([...ALL_STAGES]);
  }

  function resetDefault() {
    onChange(defaultStageFilter());
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors',
          'min-h-[32px]',
          activeCount > 0
            ? 'border-accent/40 bg-accent-soft text-accent'
            : 'border-border text-text-muted hover:border-border-strong hover:text-text'
        )}
      >
        <span className="font-medium">Stage</span>
        {activeCount > 0 && (
          <span className="rounded-full bg-accent/20 px-1.5 text-[10px] font-semibold">
            {activeCount}
          </span>
        )}
        <ChevronDown size={12} strokeWidth={2} className={cn('transition-transform', open && 'rotate-180')} />
      </button>
      {activeCount > 0 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); resetDefault(); }}
          className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-text-dim hover:text-text"
        >
          <X size={10} strokeWidth={2} />
        </button>
      )}
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-52 rounded-lg border border-border bg-surface p-1 shadow-lg max-h-72 overflow-y-auto">
          <div className="flex items-center justify-between px-2.5 pb-1 pt-0.5">
            <button
              type="button"
              onClick={selectAll}
              className="text-[10px] text-text-dim hover:text-text"
            >
              Show all
            </button>
            <button
              type="button"
              onClick={resetDefault}
              className="text-[10px] text-text-dim hover:text-text"
            >
              Reset default
            </button>
          </div>
          {ALL_STAGES.map((stage) => {
            const active = selected.includes(stage);
            return (
              <button
                key={stage}
                type="button"
                onClick={() => toggleStage(stage)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-xs',
                  'min-h-[32px]',
                  active ? 'bg-accent-soft text-accent' : 'text-text hover:bg-border/30',
                  TERMINAL_STAGES.has(stage) && !active && 'opacity-60',
                )}
              >
                <span
                  className={cn(
                    'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border',
                    active ? 'border-accent bg-accent text-white' : 'border-border'
                  )}
                >
                  {active && <span className="text-[8px]">&#10003;</span>}
                </span>
                <span className="truncate">{DISPLAY[stage] || stage}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
